import { useState, useEffect } from 'react'
import { Link } from '@inertiajs/react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import authService from '@/lib/auth'

interface LoginDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
})

type LoginFormData = z.infer<typeof loginSchema>

export function LoginDialog({ open, onOpenChange, onSuccess }: LoginDialogProps) {
  const [error, setError] = useState('')

  const { register, handleSubmit, setValue, reset, formState: { errors: formErrors, isSubmitting } } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })

  // Notify parent frame when modal opens/closes
  useEffect(() => {
    if (open) {
      window.dispatchEvent(new CustomEvent('cayu:on-login-page'))
    } else {
      window.dispatchEvent(new CustomEvent('cayu:left-login-page'))
    }
  }, [open])

  // Listen for credential fill requests from parent frame (via cayu-sdk event)
  useEffect(() => {
    if (!open) return

    const handleFillCredentials = (event: Event) => {
      const { email, password } = (event as CustomEvent<{ email: string; password: string }>).detail
      if (email && password) {
        setValue('email', email, { shouldValidate: true })
        setValue('password', password, { shouldValidate: true })
      }
    }
    window.addEventListener('cayu:fill-credentials', handleFillCredentials)
    return () => window.removeEventListener('cayu:fill-credentials', handleFillCredentials)
  }, [open, setValue])

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      reset()
      setError('')
    }
  }, [open, reset])

  const onSubmit = async (formData: LoginFormData) => {
    setError('')

    try {
      const response = await fetch('/session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      const data = await response.json()

      if (response.ok) {
        authService.handleLoginResponse(data)
        onSuccess?.()
      } else {
        setError(data.error || 'Login failed')
      }
    } catch (err) {
      setError('An error occurred during login')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center">
          <DialogTitle className="text-2xl">Welcome Back</DialogTitle>
          <DialogDescription>Sign in to your account to continue</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="login-email">Email</Label>
            <Input
              id="login-email"
              type="email"
              placeholder="you@example.com"
              {...register('email')}
            />
            {formErrors.email && (
              <p className="text-sm text-destructive">{formErrors.email.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="login-password">Password</Label>
            <Input
              id="login-password"
              type="password"
              placeholder="••••••••"
              {...register('password')}
            />
            {formErrors.password && (
              <p className="text-sm text-destructive">{formErrors.password.message}</p>
            )}
          </div>

          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? 'Signing in...' : 'Sign In'}
          </Button>

          <div className="text-center text-sm">
            <Link href="/password/forgot" className="text-muted-foreground hover:text-primary">
              Forgot your password?
            </Link>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
